import CheckboxInput from 'components/forms/inputs/rhf-inputs/CheckboxInput.tsx';
import { ModalFooter } from 'components/modals/ModalFooter.tsx';
import { ModalHeader } from 'components/modals/ModalHeader.tsx';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { cn } from 'utils/cn';
import React, { ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { CustomInvoiceForm, CustomizationDto } from 'types/invoices.ts';
import { successAlert } from 'utils/alert';
import { t } from 'utils/getTranslations.ts';
import { logError } from 'utils/monitoring';
import { useHasPermissions } from 'utils/permissionUtil.ts';

interface CustomizeInvoiceModalProps {
    invoiceId?: number;
    customization?: Partial<CustomizationDto> | null;
    onClose: () => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- callers pass either an RTK Query mutation trigger (DetailsCard, expects `{ invoiceId: number } & UpdateCustomizationDto`) or a synthetic callback that ignores invoiceId (MatterPageForm). The required/optional contravariance on invoiceId prevents a unified narrower signature without changing the out-of-batch MatterPageForm caller.
    customizeMutation: (dto: any) => { unwrap: () => Promise<unknown> };
    /** A line under the title, which the new design's side sheet draws. */
    description?: ReactNode;
}

/** Each column's sections as fieldset-like groups: the heading over its options, the options a 24px row each. */
const CUSTOMISE_COLUMNS_CLASS = cn(
    'grid grid-cols-2 items-start gap-x-10 max-sm:grid-cols-1',
    '[&_h6]:mb-3 [&_h6]:text-base [&_h6]:font-medium [&_label]:min-h-6 [&>div>div]:gap-0'
);

const CustomizeInvoiceModal: React.FC<CustomizeInvoiceModalProps> = ({
    invoiceId,
    customization,
    onClose,
    customizeMutation,
    description,
}) => {
    const { handleSubmit, control, watch, setValue } = useForm<CustomInvoiceForm>({
        defaultValues: {
            isServicePeriodDisplayed: customization?.isServicePeriodDisplayed,
            isFeeDateDisplayed: customization?.isFeeDateDisplayed,
            isFeeDoneByDisplayed: customization?.isFeeDoneByDisplayed,
            isFeePositionDisplayed: customization?.isFeePositionDisplayed,
            isFeeHourlyRateDisplayed: customization?.isFeeHourlyRateDisplayed,
            isCoseDisplayed: customization?.isCoseDisplayed,
            isFeeDescriptionDisplayed: customization?.isFeeDescriptionDisplayed,
            isFeeUsedHoursDisplayed: customization?.isFeeUsedHoursDisplayed,
            isFeeBilledHoursDisplayed: customization?.isFeeBilledHoursDisplayed,
            isFeeAmountDisplayed: customization?.isFeeAmountDisplayed,
            isFeeSummedAmountDisplayed: customization?.isFeeSummedAmountDisplayed,
            isFeeSummedBilledHoursDisplayed: customization?.isFeeSummedBilledHoursDisplayed,
            isFeeGrandTotalDisplayed: customization?.isFeeGrandTotalDisplayed,
            isExpenseDateDisplayed: customization?.isExpenseDateDisplayed,
            isExpenseAmountDisplayed: customization?.isExpenseAmountDisplayed,
            isSummedExpenseAmountDisplayed: customization?.isSummedExpenseAmountDisplayed,
            isExpenseTypeDisplayed: customization?.isExpenseTypeDisplayed,
            isExpenseDetailsDisplayed: customization?.isExpenseDetailsDisplayed,
            isExpenseGrandTotalDisplayed: customization?.isExpenseGrandTotalDisplayed,
            isPayerPrimaryRecipientDisplayed: customization?.isPayerPrimaryRecipientDisplayed,
            isRegOrPersonalCodeDisplayed: customization?.isRegOrPersonalCodeDisplayed,
            isSignedByDisplayed: customization?.isSignedByDisplayed,
            isSignedByPositionDisplayed: customization?.isSignedByPositionDisplayed,
            isExchangeRateDisplayed: customization?.isExchangeRateDisplayed,
            isTotalsGrandDisplayed: customization?.isTotalsGrandDisplayed,
            isExpenseOneLineDisplayed: customization?.isExpenseOneLineDisplayed,
            isExpenseSummaryDisplayed: customization?.isExpenseSummaryDisplayed,
            isAssignmentSummaryDisplayed: customization?.isAssignmentSummaryDisplayed,
            isTotalsAggregatedByMatterDisplayed: customization?.isTotalsAggregatedByMatterDisplayed,
            isTotalsLegalFeesAndExpensesDisplayed: customization?.isTotalsLegalFeesAndExpensesDisplayed,
            isAssignmentSummaryWithoutLawyerNamesDisplayed:
                customization?.isAssignmentSummaryWithoutLawyerNamesDisplayed,
            isAssignmentSummaryWithoutAssignmentNamesDisplayed:
                customization?.isAssignmentSummaryWithoutAssignmentNamesDisplayed,
            isSortedByLawyerName: customization?.isSortedByLawyerName,
            isOutlineDisplayed: customization?.isOutlineDisplayed,
            isBreakdownDisplayed: customization?.isBreakdownDisplayed,
            isAdjustmentsConsideredDiscounts: customization?.isAdjustmentsConsideredDiscounts,
            isSpecFeeHourlyRateDisplayed: customization?.isSpecFeeHourlyRateDisplayed,
            isCaseSupervisorDisplayed: customization?.isCaseSupervisorDisplayed,
            isDiscountDisplayed: customization?.isDiscountDisplayed,
            isVatLegalReferenceHidden: customization?.isVatLegalReferenceHidden,
            isPrimaryRecipientHidden: customization?.isPrimaryRecipientHidden,
            isBankAddressDisplayed: customization?.isBankAddressDisplayed,
            isDiscountRowHidden: customization?.isDiscountRowHidden,
            isInvoiceDateDisplayed: customization?.isInvoiceDateDisplayed,
            isBuyerNumberDisplayed: customization?.isBuyerNumberDisplayed,
        },
    });

    const hasPermissionToUpdate = useHasPermissions(['UPDATE.CUSTOMIZE_INVOICE_SPEC_TEMPLATE']);
    const isNew = useIsNewDesign();
    const isSignedByDisplayed = watch('isSignedByDisplayed');

    const onSave = () => {
        handleSubmit(async (data) => {
            const dto = mapToDto(data);
            await customizeMutation({ invoiceId, ...dto })
                .unwrap()
                .then(async () => {
                    if (invoiceId) {
                        successAlert(t('invoices.custom.customization-saved'));
                    }
                })
                .then(() => {
                    onClose();
                })
                .catch((error) => {
                    console.error(error);
                    logError(error);
                });
        })();
    };

    const feesSection =
        customization?.isFeeDateDisplayed != null ||
        customization?.isFeeDoneByDisplayed != null ||
        customization?.isFeePositionDisplayed != null ||
        customization?.isFeeHourlyRateDisplayed != null ||
        customization?.isFeeDescriptionDisplayed != null ||
        customization?.isFeeUsedHoursDisplayed != null ||
        customization?.isFeeBilledHoursDisplayed != null ||
        customization?.isFeeAmountDisplayed != null ||
        customization?.isFeeSummedBilledHoursDisplayed != null ||
        customization?.isFeeSummedAmountDisplayed != null ||
        customization?.isFeeGrandTotalDisplayed != null ? (
            <div className="flex flex-col gap-4">
                <h6 className="text-h6">
                    <FormattedMessage id={'invoices.custom.fees'} />
                </h6>
                {customization?.isFeeDateDisplayed != null ? (
                    <CheckboxInput
                        disabled={!hasPermissionToUpdate}
                        control={control}
                        name="isFeeDateDisplayed"
                        label="invoices.custom.date"
                    />
                ) : null}
                {customization?.isFeeDoneByDisplayed != null ? (
                    <CheckboxInput
                        disabled={!hasPermissionToUpdate}
                        control={control}
                        name="isFeeDoneByDisplayed"
                        label="invoices.custom.done-by"
                    />
                ) : null}
                {customization?.isFeePositionDisplayed != null ? (
                    <CheckboxInput
                        disabled={!hasPermissionToUpdate}
                        control={control}
                        name="isFeePositionDisplayed"
                        label="invoices.custom.position"
                    />
                ) : null}
                {customization?.isFeeHourlyRateDisplayed != null ? (
                    <CheckboxInput
                        disabled={!hasPermissionToUpdate}
                        control={control}
                        name="isFeeHourlyRateDisplayed"
                        label="invoices.custom.hourly-rate"
                    />
                ) : null}
                {customization?.isFeeDescriptionDisplayed != null ? (
                    <CheckboxInput
                        disabled={!hasPermissionToUpdate}
                        control={control}
                        name="isFeeDescriptionDisplayed"
                        label="invoices.custom.description"
                    />
                ) : null}
                {customization?.isFeeUsedHoursDisplayed != null ? (
                    <CheckboxInput
                        disabled={!hasPermissionToUpdate}
                        control={control}
                        name="isFeeUsedHoursDisplayed"
                        label="invoices.custom.used-hours"
                    />
                ) : null}
                {customization?.isFeeBilledHoursDisplayed != null ? (
                    <CheckboxInput
                        disabled={!hasPermissionToUpdate}
                        control={control}
                        name="isFeeBilledHoursDisplayed"
                        label="invoices.custom.billed-hours"
                    />
                ) : null}
                {customization?.isFeeAmountDisplayed != null ? (
                    <CheckboxInput
                        disabled={!hasPermissionToUpdate}
                        control={control}
                        name="isFeeAmountDisplayed"
                        label="invoices.custom.amount"
                    />
                ) : null}
                {customization?.isFeeSummedBilledHoursDisplayed != null ? (
                    <CheckboxInput
                        disabled={!hasPermissionToUpdate}
                        control={control}
                        name="isFeeSummedBilledHoursDisplayed"
                        label="invoices.custom.summed-billed-hours"
                    />
                ) : null}
                {customization?.isFeeSummedAmountDisplayed != null ? (
                    <CheckboxInput
                        disabled={!hasPermissionToUpdate}
                        control={control}
                        name="isFeeSummedAmountDisplayed"
                        label="invoices.custom.summed-amount"
                    />
                ) : null}
                {customization?.isFeeGrandTotalDisplayed != null ? (
                    <CheckboxInput
                        disabled={!hasPermissionToUpdate}
                        control={control}
                        name="isFeeGrandTotalDisplayed"
                        label="invoices.custom.grand-total"
                    />
                ) : null}
            </div>
        ) : null;
    const expensesSection =
        customization?.isExpenseDateDisplayed != null ||
        customization?.isExpenseTypeDisplayed != null ||
        customization?.isExpenseDetailsDisplayed != null ||
        customization?.isExpenseAmountDisplayed != null ||
        customization?.isSummedExpenseAmountDisplayed != null ||
        customization?.isExpenseGrandTotalDisplayed != null ? (
            <div className="flex flex-col gap-4">
                <h6 className="text-h6">
                    <FormattedMessage id={'invoices.custom.expenses'} />
                </h6>
                {customization?.isExpenseDateDisplayed != null ? (
                    <CheckboxInput
                        disabled={!hasPermissionToUpdate}
                        control={control}
                        name="isExpenseDateDisplayed"
                        label="invoices.custom.expense-date"
                    />
                ) : null}
                {customization?.isExpenseTypeDisplayed != null ? (
                    <CheckboxInput
                        disabled={!hasPermissionToUpdate}
                        control={control}
                        name="isExpenseTypeDisplayed"
                        label="invoices.custom.expense-type"
                    />
                ) : null}
                {customization?.isExpenseDetailsDisplayed != null ? (
                    <CheckboxInput
                        disabled={!hasPermissionToUpdate}
                        control={control}
                        name="isExpenseDetailsDisplayed"
                        label="invoices.custom.expense-details"
                    />
                ) : null}
                {customization?.isExpenseAmountDisplayed != null ? (
                    <CheckboxInput
                        disabled={!hasPermissionToUpdate}
                        control={control}
                        name="isExpenseAmountDisplayed"
                        label="invoices.custom.expense-amount"
                    />
                ) : null}
                {customization?.isSummedExpenseAmountDisplayed != null ? (
                    <CheckboxInput
                        disabled={!hasPermissionToUpdate}
                        control={control}
                        name="isSummedExpenseAmountDisplayed"
                        label="invoices.custom.summed-expense-amount"
                    />
                ) : null}
                {customization?.isExpenseGrandTotalDisplayed != null ? (
                    <CheckboxInput
                        disabled={!hasPermissionToUpdate}
                        control={control}
                        name="isExpenseGrandTotalDisplayed"
                        label="invoices.custom.grand-total"
                    />
                ) : null}
            </div>
        ) : null;
    const otherSection =
        customization?.isServicePeriodDisplayed != null ||
        customization?.isPayerPrimaryRecipientDisplayed != null ||
        customization?.isRegOrPersonalCodeDisplayed != null ||
        customization?.isSignedByDisplayed != null ||
        customization?.isSignedByPositionDisplayed != null ||
        customization?.isBreakdownDisplayed != null ||
        customization?.isOutlineDisplayed != null ||
        customization?.isVatLegalReferenceHidden != null ||
        customization?.isPrimaryRecipientHidden != null ||
        customization?.isBankAddressDisplayed != null ||
        customization?.isDiscountRowHidden != null ||
        customization?.isInvoiceDateDisplayed != null ||
        customization?.isBuyerNumberDisplayed != null ? (
            <div className="flex flex-col gap-4">
                <h6 className="text-h6">
                    <FormattedMessage id={'invoices.custom.other'} />
                </h6>
                {customization?.isServicePeriodDisplayed != null ? (
                    <CheckboxInput
                        disabled={!hasPermissionToUpdate}
                        control={control}
                        name="isServicePeriodDisplayed"
                        label="invoices.custom.service-period"
                    />
                ) : null}
                {customization?.isPayerPrimaryRecipientDisplayed != null ? (
                    <CheckboxInput
                        disabled={!hasPermissionToUpdate}
                        control={control}
                        name="isPayerPrimaryRecipientDisplayed"
                        label="invoices.custom.primary-recipient"
                    />
                ) : null}
                {customization?.isRegOrPersonalCodeDisplayed != null ? (
                    <CheckboxInput
                        disabled={!hasPermissionToUpdate}
                        control={control}
                        name="isRegOrPersonalCodeDisplayed"
                        label="invoices.custom.reg-or-personal-code"
                    />
                ) : null}
                {customization?.isVatLegalReferenceHidden != null ? (
                    <CheckboxInput
                        disabled={!hasPermissionToUpdate}
                        control={control}
                        name="isVatLegalReferenceHidden"
                        label="invoices.custom.hide-vat-legal-reference"
                    />
                ) : null}
                {customization?.isPrimaryRecipientHidden != null ? (
                    <CheckboxInput
                        disabled={!hasPermissionToUpdate}
                        control={control}
                        name="isPrimaryRecipientHidden"
                        label="invoices.custom.hide-primary-recipient"
                    />
                ) : null}
                {customization?.isBankAddressDisplayed != null ? (
                    <CheckboxInput
                        disabled={!hasPermissionToUpdate}
                        control={control}
                        name="isBankAddressDisplayed"
                        label="invoices.custom.bank-address"
                    />
                ) : null}
                {customization?.isDiscountRowHidden != null ? (
                    <CheckboxInput
                        disabled={!hasPermissionToUpdate}
                        control={control}
                        name="isDiscountRowHidden"
                        label="invoices.custom.hide-discount-row"
                    />
                ) : null}
                {customization?.isInvoiceDateDisplayed != null ? (
                    <CheckboxInput
                        disabled={!hasPermissionToUpdate}
                        control={control}
                        name="isInvoiceDateDisplayed"
                        label="invoices.custom.invoice-date"
                    />
                ) : null}
                {customization?.isBuyerNumberDisplayed != null ? (
                    <CheckboxInput
                        disabled={!hasPermissionToUpdate}
                        control={control}
                        name="isBuyerNumberDisplayed"
                        label="invoices.custom.buyer-number"
                    />
                ) : null}
                {customization?.isSignedByDisplayed != null ? (
                    <CheckboxInput
                        disabled={!hasPermissionToUpdate}
                        control={control}
                        name="isSignedByDisplayed"
                        label="invoices.custom.signed-by"
                        onChange={(checked) => {
                            if (!checked) {
                                setValue('isSignedByPositionDisplayed', false);
                            }
                        }}
                    />
                ) : null}
                {customization?.isSignedByPositionDisplayed != null ? (
                    <CheckboxInput
                        disabled={!hasPermissionToUpdate || !isSignedByDisplayed}
                        control={control}
                        name="isSignedByPositionDisplayed"
                        label="invoices.custom.signed-by-position"
                    />
                ) : null}
                {customization?.isOutlineDisplayed != null ? (
                    <CheckboxInput
                        disabled={!hasPermissionToUpdate || !isSignedByDisplayed}
                        control={control}
                        name="isOutlineDisplayed"
                        label="invoices.custom.outline-displayed"
                        onChange={(checked) => {
                            if (!checked) {
                                setValue('isBreakdownDisplayed', true);
                            }
                        }}
                    />
                ) : null}
                {customization?.isBreakdownDisplayed != null ? (
                    <CheckboxInput
                        disabled={!hasPermissionToUpdate || !isSignedByDisplayed}
                        control={control}
                        name="isBreakdownDisplayed"
                        label="invoices.custom.breakdown-displayed"
                        onChange={(checked) => {
                            if (!checked) {
                                setValue('isOutlineDisplayed', true);
                            }
                        }}
                    />
                ) : null}
            </div>
        ) : null;
    const outlineSection =
        customization?.isExchangeRateDisplayed != null ||
        customization?.isTotalsGrandDisplayed != null ||
        customization?.isTotalsAggregatedByMatterDisplayed != null ||
        customization?.isTotalsLegalFeesAndExpensesDisplayed != null ||
        customization?.isExpenseOneLineDisplayed != null ||
        customization?.isExpenseSummaryDisplayed != null ||
        customization?.isAssignmentSummaryDisplayed != null ||
        customization?.isAssignmentSummaryWithoutLawyerNamesDisplayed != null ||
        customization?.isAssignmentSummaryWithoutAssignmentNamesDisplayed != null ||
        customization?.isAdjustmentsConsideredDiscounts != null ||
        customization?.isDiscountDisplayed != null ||
        customization?.isCaseSupervisorDisplayed != null ? (
            <div className="flex flex-col gap-4">
                <h6 className="text-h6">
                    <FormattedMessage id={'invoices.custom.outline'} />
                </h6>
                {customization?.isExchangeRateDisplayed != null ? (
                    <CheckboxInput
                        disabled={!hasPermissionToUpdate}
                        control={control}
                        name="isExchangeRateDisplayed"
                        label="invoices.custom.exchange-rate"
                    />
                ) : null}
                {customization?.isTotalsGrandDisplayed != null ? (
                    <CheckboxInput
                        disabled={!hasPermissionToUpdate}
                        control={control}
                        name="isTotalsGrandDisplayed"
                        label="invoices.custom.totals-grand"
                        onChange={(checked) => {
                            if (checked) {
                                setValue('isTotalsAggregatedByMatterDisplayed', false);
                                setValue('isTotalsLegalFeesAndExpensesDisplayed', false);
                            }
                        }}
                    />
                ) : null}
                {customization?.isTotalsAggregatedByMatterDisplayed != null ? (
                    <CheckboxInput
                        disabled={!hasPermissionToUpdate}
                        control={control}
                        name="isTotalsAggregatedByMatterDisplayed"
                        label="invoices.custom.totals-aggregated-by-matter"
                        onChange={(checked) => {
                            if (checked) {
                                setValue('isTotalsGrandDisplayed', false);
                                setValue('isTotalsLegalFeesAndExpensesDisplayed', false);
                                setValue('isExpenseOneLineDisplayed', false);
                                setValue('isExpenseSummaryDisplayed', false);
                                setValue('isAssignmentSummaryDisplayed', false);
                                setValue('isAssignmentSummaryWithoutLawyerNamesDisplayed', false);
                                setValue('isAssignmentSummaryWithoutAssignmentNamesDisplayed', false);
                                setValue('isAdjustmentsConsideredDiscounts', false);
                            }
                        }}
                    />
                ) : null}
                {customization?.isTotalsLegalFeesAndExpensesDisplayed != null ? (
                    <CheckboxInput
                        disabled={!hasPermissionToUpdate}
                        control={control}
                        name="isTotalsLegalFeesAndExpensesDisplayed"
                        label="invoices.custom.totals-legal-fees-and-expenses"
                        onChange={(checked) => {
                            if (checked) {
                                setValue('isTotalsGrandDisplayed', false);
                                setValue('isTotalsAggregatedByMatterDisplayed', false);
                            }
                        }}
                    />
                ) : null}
                {customization?.isExpenseOneLineDisplayed != null ? (
                    <CheckboxInput
                        disabled={!hasPermissionToUpdate}
                        control={control}
                        name="isExpenseOneLineDisplayed"
                        label="invoices.custom.expense-one-line"
                        onChange={(checked) => {
                            if (checked) {
                                setValue('isExpenseSummaryDisplayed', false);
                            }
                        }}
                    />
                ) : null}
                {customization?.isExpenseSummaryDisplayed != null ? (
                    <CheckboxInput
                        disabled={!hasPermissionToUpdate}
                        control={control}
                        name="isExpenseSummaryDisplayed"
                        label="invoices.custom.expense-summary"
                        onChange={(checked) => {
                            if (checked) {
                                setValue('isExpenseOneLineDisplayed', false);
                            }
                        }}
                    />
                ) : null}
                {customization?.isAssignmentSummaryDisplayed != null ? (
                    <CheckboxInput
                        disabled={!hasPermissionToUpdate}
                        control={control}
                        name="isAssignmentSummaryDisplayed"
                        label="invoices.custom.assignment-summary"
                        onChange={(checked) => {
                            if (checked) {
                                setValue('isAssignmentSummaryWithoutAssignmentNamesDisplayed', false);
                                setValue('isAssignmentSummaryWithoutLawyerNamesDisplayed', false);
                            }
                        }}
                    />
                ) : null}
                {customization?.isAssignmentSummaryWithoutLawyerNamesDisplayed != null ? (
                    <CheckboxInput
                        disabled={!hasPermissionToUpdate}
                        control={control}
                        name="isAssignmentSummaryWithoutLawyerNamesDisplayed"
                        label="invoices.custom.assignment-summary-wo-lawyer-name"
                        onChange={(checked) => {
                            if (checked) {
                                setValue('isAssignmentSummaryWithoutAssignmentNamesDisplayed', false);
                                setValue('isAssignmentSummaryDisplayed', false);
                                setValue('isFeeHourlyRateDisplayed', false);
                            }
                        }}
                    />
                ) : null}
                {customization?.isAssignmentSummaryWithoutAssignmentNamesDisplayed != null ? (
                    <CheckboxInput
                        disabled={!hasPermissionToUpdate}
                        control={control}
                        name="isAssignmentSummaryWithoutAssignmentNamesDisplayed"
                        label="invoices.custom.assignment-summary-wo-assignment-name"
                        onChange={(checked) => {
                            if (checked) {
                                setValue('isAssignmentSummaryWithoutLawyerNamesDisplayed', false);
                                setValue('isAssignmentSummaryDisplayed', false);
                            }
                        }}
                    />
                ) : null}
                {customization?.isAdjustmentsConsideredDiscounts != null ? (
                    <CheckboxInput
                        disabled={!hasPermissionToUpdate}
                        control={control}
                        name="isAdjustmentsConsideredDiscounts"
                        label="invoices.custom.adjustments-considered-as-discounts"
                    />
                ) : null}
                {customization?.isCaseSupervisorDisplayed != null ? (
                    <CheckboxInput
                        disabled={!hasPermissionToUpdate}
                        control={control}
                        name="isCaseSupervisorDisplayed"
                        label="invoices.custom.case-supervisor"
                    />
                ) : null}
                {customization?.isDiscountDisplayed != null ? (
                    <CheckboxInput
                        disabled={!hasPermissionToUpdate}
                        control={control}
                        name="isDiscountDisplayed"
                        label="invoices.custom.discount"
                    />
                ) : null}
            </div>
        ) : null;
    const specSection =
        customization?.isSortedByLawyerName != null || customization?.isSpecFeeHourlyRateDisplayed != null ? (
            <div className="flex flex-col gap-4">
                <h6 className="text-h6">
                    <FormattedMessage id={'invoices.custom.spec'} />
                </h6>
                {customization?.isSortedByLawyerName != null ? (
                    <CheckboxInput
                        disabled={!hasPermissionToUpdate}
                        control={control}
                        name="isSortedByLawyerName"
                        label="invoices.custom.sort-by-lawyers-name"
                    />
                ) : null}
                {customization?.isSpecFeeHourlyRateDisplayed != null ? (
                    <CheckboxInput
                        disabled={!hasPermissionToUpdate}
                        control={control}
                        name="isSpecFeeHourlyRateDisplayed"
                        label="invoices.custom.hourly-rate"
                    />
                ) : null}
            </div>
        ) : null;

    return (
        <>
            <ModalHeader title={t('invoices.custom.customize')} description={description} onCancel={onClose} />
            <div className="h-full overflow-y-auto px-6 py-3 max-sm:px-4 max-sm:py-6">
                {isNew ? (
                    // The new design's two columns: what the rows show on the left, the invoice's own options on the right.
                    <div className={CUSTOMISE_COLUMNS_CLASS}>
                        <div className="flex min-w-0 flex-col gap-6">
                            {feesSection}
                            {expensesSection}
                            {specSection}
                        </div>
                        <div className="flex min-w-0 flex-col gap-6">
                            {otherSection}
                            {outlineSection}
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-row gap-4">
                        {feesSection}
                        {expensesSection}
                        {otherSection}
                        {outlineSection}
                        {specSection}
                    </div>
                )}
            </div>
            <ModalFooter readOnly={!hasPermissionToUpdate} onCancel={onClose} onSave={onSave} />
        </>
    );
};

export default CustomizeInvoiceModal;

const mapToDto = (data: CustomInvoiceForm) => {
    return {
        isFeeDateDisplayed: data.isFeeDateDisplayed,
        isServicePeriodDisplayed: data.isServicePeriodDisplayed,
        isFeeDoneByDisplayed: data.isFeeDoneByDisplayed,
        isFeePositionDisplayed: data.isFeePositionDisplayed,
        isFeeHourlyRateDisplayed: data.isFeeHourlyRateDisplayed,
        isCoseDisplayed: data.isCoseDisplayed,
        isFeeDescriptionDisplayed: data.isFeeDescriptionDisplayed,
        isFeeUsedHoursDisplayed: data.isFeeUsedHoursDisplayed,
        isFeeBilledHoursDisplayed: data.isFeeBilledHoursDisplayed,
        isFeeGrandTotalDisplayed: data.isFeeGrandTotalDisplayed,
        isFeeAmountDisplayed: data.isFeeAmountDisplayed,
        isFeeSummedAmountDisplayed: data.isFeeSummedAmountDisplayed,
        isFeeSummedBilledHoursDisplayed: data.isFeeSummedBilledHoursDisplayed,
        isExpenseDateDisplayed: data.isExpenseDateDisplayed,
        isExpenseAmountDisplayed: data.isExpenseAmountDisplayed,
        isSummedExpenseAmountDisplayed: data.isSummedExpenseAmountDisplayed,
        isExpenseTypeDisplayed: data.isExpenseTypeDisplayed,
        isExpenseDetailsDisplayed: data.isExpenseDetailsDisplayed,
        isExpenseGrandTotalDisplayed: data.isExpenseGrandTotalDisplayed,
        isPayerPrimaryRecipientDisplayed: data.isPayerPrimaryRecipientDisplayed,
        isRegOrPersonalCodeDisplayed: data.isRegOrPersonalCodeDisplayed,
        isSignedByDisplayed: data.isSignedByDisplayed,
        isSignedByPositionDisplayed: data.isSignedByPositionDisplayed,
        isExchangeRateDisplayed: data.isExchangeRateDisplayed,
        isTotalsGrandDisplayed: data.isTotalsGrandDisplayed,
        isExpenseOneLineDisplayed: data.isExpenseOneLineDisplayed,
        isExpenseSummaryDisplayed: data.isExpenseSummaryDisplayed,
        isAssignmentSummaryDisplayed: data.isAssignmentSummaryDisplayed,
        isTotalsAggregatedByMatterDisplayed: data.isTotalsAggregatedByMatterDisplayed,
        isTotalsLegalFeesAndExpensesDisplayed: data.isTotalsLegalFeesAndExpensesDisplayed,
        isAssignmentSummaryWithoutLawyerNamesDisplayed: data.isAssignmentSummaryWithoutLawyerNamesDisplayed,
        isAssignmentSummaryWithoutAssignmentNamesDisplayed: data.isAssignmentSummaryWithoutAssignmentNamesDisplayed,
        isSortedByLawyerName: data.isSortedByLawyerName,
        isOutlineDisplayed: data.isOutlineDisplayed,
        isBreakdownDisplayed: data.isBreakdownDisplayed,
        isAdjustmentsConsideredDiscounts: data.isAdjustmentsConsideredDiscounts,
        isSpecFeeHourlyRateDisplayed: data.isSpecFeeHourlyRateDisplayed,
        isCaseSupervisorDisplayed: data.isCaseSupervisorDisplayed,
        isDiscountDisplayed: data.isDiscountDisplayed,
        isVatLegalReferenceHidden: data.isVatLegalReferenceHidden,
        isPrimaryRecipientHidden: data.isPrimaryRecipientHidden,
        isBankAddressDisplayed: data.isBankAddressDisplayed,
        isDiscountRowHidden: data.isDiscountRowHidden,
        isInvoiceDateDisplayed: data.isInvoiceDateDisplayed,
        isBuyerNumberDisplayed: data.isBuyerNumberDisplayed,
    };
};
